import { QuoteIcon } from '@jouzen/ecom-components/icons/QuoteIcon';
import { Typography } from '@jouzen/ecom-components/v3/Typography';
import { cx } from 'class-variance-authority';
import { useTranslations } from 'next-intl';

import Image from '@/app/components/Image';
import Motion from '@/app/components/Motion';
import RichText from '@/app/components/RichText';
import useAuroraRollout from '@/app/hooks/useAuroraRollout';
import { useImageExperience } from '@/app/hooks/useImageExperience';

import MobileFeature from './MobileFeature';
import { getImageProps } from 'next/image';

const UnderWeather = () => {
  const t = useTranslations();
  // TODO: GROW-2891 Clean up aurora rollout
  const isAuroraEnabled = useAuroraRollout();
  const imageExperience = useImageExperience({
    japaneseImgObj: {
      img: 'ja-regional/hp-features-under-the-weather-desktop',
      alt: 'man_forehead_img_alt',
    },
    jadeImgObj: {
      img: 'features/under_the_weather_jade',
      alt: 'tab_item_under_weather_img_alt',
    },
    defaultImgObj: {
      img: 'blue-sky/home/under-weather',
      alt: 'tab_item_under_weather_img_alt',
    },
  });

  const { props: bodyTempMaskImageProps } = getImageProps({
    alt: '',
    height: 265,
    src: `jade/masks/bg_mask_under_the_weather_body_temp.jpg`,
    width: 300,
  });

  const { props: readinessMaskImageProps } = getImageProps({
    alt: '',
    height: 250,
    src: `jade/masks/bg_mask_under_the_weather_readiness.jpg`,
    width: 300,
  });

  return (
    <MobileFeature
      title="features_tab_3_label"
      image={{
        alt: imageExperience.alt,
        src: imageExperience.img,
      }}
    >
      <div className="relative hidden lg:block" data-cy="under-weather">
        <Image
          alt={t(imageExperience.alt)}
          src={imageExperience.img}
          height={500}
          width={450}
          className="rounded-lg object-cover"
          data-cy="under-weather-image"
        />
      </div>
      <div
        className={cx(
          'absolute bottom-16 left-1/2 w-[80vw] max-w-[300px] -translate-x-1/2 flex-col gap-y-6',
          'lg:relative lg:bottom-auto lg:left-0 lg:mt-14 lg:flex lg:w-auto lg:max-w-none lg:translate-x-0',
        )}
      >
        <div
          className="hidden grid-cols-1 grid-rows-1 overflow-hidden rounded-lg bg-cover lg:grid"
          style={{ backgroundImage: `url(${bodyTempMaskImageProps.src})` }}
          data-cy="under-weather-mask-image-body-temp"
        >
          <Image
            alt={t(
              isAuroraEnabled
                ? 'symptom_radar_image_alt'
                : 'body_temp_image_alt',
            )}
            className="col-start-1 col-end-2 row-start-1 row-end-2"
            src={t(
              isAuroraEnabled
                ? 'home_scenarios_illness_radar_ui'
                : 'body_temp_app_ui',
            )}
            format="png"
            width={300}
            height={265}
            data-cy="under-weather-illness-symptom-app-ui-image"
          />
        </div>
        <div
          className="hidden grid-cols-1 grid-rows-1 overflow-hidden rounded-lg bg-cover lg:grid"
          style={{ backgroundImage: `url(${readinessMaskImageProps.src})` }}
          data-cy="under-weather-mask-image-readiness"
        >
          <Image
            alt={t('recovery_image_alt')}
            className={cx('col-start-1 col-end-2 row-start-1 row-end-2', {
              'pt-4': isAuroraEnabled,
            })}
            src={t(
              isAuroraEnabled
                ? 'home_scenarios_illness_readiness_ui'
                : 'recovery_app_ui',
            )}
            format="png"
            width={300}
            height={252}
            data-cy="under-weather-illness-readiness-app-ui-image"
          />
        </div>
        <Motion
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1, type: 'tween', delay: 0.5 }}
          className="grid grid-cols-1 grid-rows-1 overflow-hidden rounded-lg bg-cover lg:hidden"
          style={{ backgroundImage: `url(${readinessMaskImageProps.src})` }}
          data-cy="under-weather-mask-image-readiness-mobile"
        >
          <Image
            alt={t('recovery_image_alt')}
            className={cx(
              'col-start-1 col-end-2 row-start-1 row-end-2 self-center',
              { 'pt-4': isAuroraEnabled },
            )}
            src={t(
              isAuroraEnabled
                ? 'home_scenarios_illness_readiness_ui'
                : 'recovery_app_ui',
            )}
            data-cy="under-weather-illness-readiness-app-ui-image"
            format="png"
            width={300}
            height={252}
          />
        </Motion>
      </div>
      <div className="mt-14 hidden max-w-[304px] self-start rounded-lg border border-gray-300 px-10 py-12 lg:block">
        <QuoteIcon className="text-olive-700" />
        <Typography
          className="mt-24 text-3xl"
          font="serif"
          weight="thin"
          variant="unset"
        >
          <RichText messageKey="features_tab_3_quote" />
        </Typography>
        <Typography
          className="mt-4 text-olive-700"
          variant="body-small"
          color="unset"
        >
          Linda D., {t('oura_member')}
        </Typography>
      </div>
    </MobileFeature>
  );
};

export default UnderWeather;
